// ConnectivityIndicator — the lost-connection banner that appears/disappears
// with the connection. (The always-on signal icon was removed 2026-10-01.)
//
// It reads the single global status from useConnectivity() (navigator.onLine +
// the shared Supabase realtime socket — see utils/connectivity.js). Analytics for
// drops/recoveries are fired centrally there, so this is presentation-only.
//
// Motion uses framer-motion + the shared DUR tokens, matching NetworkStatus.jsx.
// (No GSAP here, so the anims.js rule doesn't apply; the pulse is a CSS utility.)

import { useState, useEffect, useRef } from 'react';
import { track } from '@tasted/utils/analytics';
import { AnimatePresence, motion as Motion } from 'framer-motion';
import { SignalLow, SignalZero } from 'lucide-react';
import { DUR, liteMotion } from '../../lib/anim-tokens';
import { useConnectivity } from '../../hooks/useConnectivity';
import { recoverSession } from '../../utils/sessionRecovery';

// True when the current route is the live game (/game or /game/*). Read from
// window rather than useLocation() so the banner has no hard Router dependency
// (it renders on every route incl. contexts/tests without a Router). It re-reads
// on each render, and renders are driven by connection-status changes + the App
// re-rendering on navigation, so it stays fresh enough for a copy switch.
function isInGameRoute() {
  if (typeof window === 'undefined') return false;
  const p = window.location.pathname;
  return p === '/game' || p.startsWith('/game/');
}

/**
 * Manual recovery affordance shown while the socket is dropped but the device is
 * still online (the "connected-but-stuck" case). Re-authenticates and re-reads
 * authoritative state rather than repeating the failed read (TAS-181). The
 * automatic reconcile usually handles this; this is the escape hatch when it
 * doesn't. Full-offline devices don't get it — there's nothing to reach.
 */
function ReloadStateButton() {
  const [pending, setPending] = useState(false);
  const onClick = async () => {
    if (pending) return;
    setPending(true);
    try {
      await recoverSession();
    } finally {
      // recoverSession may reload the page (unmounting us); guard for the case
      // where it just reconciled in place and we're still mounted.
      setPending(false);
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={pending}
      className="shrink-0 underline underline-offset-2 decoration-white/60 hover:decoration-white disabled:opacity-60"
    >
      {pending ? 'Reloading…' : 'Reload game state'}
    </button>
  );
}

/**
 * Full-width banner that slides in while the connection is degraded and slides
 * out on recovery. Amber for a realtime drop, red for a full offline device.
 */
export function ConnectivityBanner() {
  const status = useConnectivity();
  const show = status !== 'online';
  const offline = status === 'offline';
  // TAS-180: the moment a player is TOLD the connection is degraded is the
  // moment it becomes their experience — count each showing, and how long it
  // stayed up (fired on hide).
  const shownAtRef = useRef(null);
  useEffect(() => {
    if (show && shownAtRef.current == null) {
      shownAtRef.current = Date.now();
      try { track('Reconnecting Banner Shown', { status, inGame: isInGameRoute() }); } catch { /* */ }
    } else if (!show && shownAtRef.current != null) {
      const ms = Date.now() - shownAtRef.current;
      shownAtRef.current = null;
      try { track('Reconnecting Banner Hidden', { durationMs: ms, inGame: isInGameRoute() }); } catch { /* */ }
    }
  }, [show, status]);

  // TAS-187: inside an active game, make the banner game-aware — the flow is
  // GM-gated so an offline player just PAUSES for themselves, and on reconnect
  // the reconcile re-reads the current step + answer count automatically (no
  // refresh). Say so, so a dropped player knows their spot is held and they'll
  // be caught up, rather than fearing they've been left behind.
  const inGame = isInGameRoute();

  const message = offline
    ? inGame
      ? "You're offline — the game's paused for you. We'll catch you up the moment you reconnect."
      : "You're offline — we'll reconnect you automatically."
    : inGame
      ? "Reconnecting — hang tight, we'll catch you up…"
      : 'Connection dropped — reconnecting…';

  return (
    <AnimatePresence>
      {show && (
        <Motion.div
          key={status}
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: DUR.med }}
          className={`fixed top-0 inset-x-0 z-[59] px-4 py-2 text-center text-sm font-medium text-white ${
            offline ? 'bg-red-500' : 'bg-amber-500'
          }`}
          role="status"
          aria-live="polite"
        >
          <div className="flex items-center justify-center gap-2">
            {offline ? (
              <SignalZero aria-hidden="true" className="h-4 w-4 shrink-0" />
            ) : (
              <SignalLow aria-hidden="true" className={`h-4 w-4 shrink-0 ${liteMotion() ? '' : 'animate-pulse'}`} />
            )}
            <span>{message}</span>
            {!offline && <ReloadStateButton />}
          </div>
        </Motion.div>
      )}
    </AnimatePresence>
  );
}
